import type {
	ApplicationEventI,
	FieldInteractionEventI,
	FieldKeystrokeMetricsI,
	GetApplicationOneResponseI,
	KeystrokeEventI,
} from "@Common/types/bo/applications/applications";
import type { ComponentPropI } from "@Common/types/resources";
import { renderFieldView } from "@CommonLib/src/frontend/WizardForm/RenderField";
import { KeystrokeReplayPlayer } from "@Src/components/KeystrokeReplayPlayer";
import { ApplicationAnalytics } from "@Src/resources/applications/ApplicationAnalytics";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import {
	Alert,
	Box,
	Button,
	Chip,
	Collapse,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";
import {
	BooleanField,
	FunctionField,
	Show,
	SimpleShowLayout,
	TextField,
	useNotify,
	useRecordContext,
	useRefresh,
	useUpdate,
} from "react-admin";

type ApplicationOneResponseI = GetApplicationOneResponseI["responses"][number];

type TimelineEvent =
	| {
			type: "field-response";
			timestamp: number | null;
			response: ApplicationOneResponseI;
			keystrokeMetrics: FieldKeystrokeMetricsI | null;
			keystrokeEvents: KeystrokeEventI[] | null;
	  }
	| { type: "app-event"; timestamp: number; eventType: string };

const formatTimeSpent = (seconds: number): string => {
	const mins = Math.floor(seconds / 60);
	const secs = Math.round(seconds % 60);
	if (mins > 0) return `${mins}m ${secs}s`;
	return `${secs}s`;
};

const formatTimestamp = (ts: number): string => {
	const date = new Date(ts);
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	const h = String(date.getUTCHours()).padStart(2, "0");
	const min = String(date.getUTCMinutes()).padStart(2, "0");
	const s = String(date.getUTCSeconds()).padStart(2, "0");
	return `${y}-${m}-${d} ${h}:${min}:${s} UTC`;
};

const KeystrokeMetricsDisplay = ({
	metrics,
}: {
	metrics: FieldKeystrokeMetricsI;
}) => {
	const isWarning =
		metrics.longest_pause_ms !== null && metrics.longest_pause_ms > 30000;

	const parts: string[] = [];
	if (metrics.time_spent_seconds !== null) {
		parts.push(formatTimeSpent(metrics.time_spent_seconds));
	}
	parts.push(`${metrics.keystroke_count} keystrokes`);
	if (metrics.typing_speed_keys_per_min !== null) {
		parts.push(`${metrics.typing_speed_keys_per_min} keys/min`);
	}
	if (metrics.longest_pause_ms !== null) {
		parts.push(
			`longest pause: ${(metrics.longest_pause_ms / 1000).toFixed(1)}s`,
		);
	}

	return (
		<Typography
			variant="caption"
			color={isWarning ? "warning.main" : "text.secondary"}
			sx={{ display: "block", mt: 0.5, mb: 1 }}
		>
			{parts.join(" \u00b7 ")}
		</Typography>
	);
};

const ReplayToggle = ({ events }: { events: KeystrokeEventI[] }) => {
	const [showReplay, setShowReplay] = useState(false);
	return (
		<Box>
			<Button
				size="small"
				variant="text"
				onClick={() => setShowReplay((prev) => !prev)}
				sx={{ textTransform: "none", mb: 0.5 }}
			>
				{showReplay ? "Hide replay" : "Show replay"}
			</Button>
			{showReplay && (
				<Typography variant="body2" sx={{ fontSize: "0.8125rem", mb: 0.5 }}>
					NOTE: Replay shows keystrokes within the field area. If replay output
					is empty, <br />
					it probably means the answer was pasted in the field.
				</Typography>
			)}
			<Collapse in={showReplay}>
				<KeystrokeReplayPlayer events={events} />
			</Collapse>
		</Box>
	);
};

const computeAverageKeystrokeTimestamp = (
	keystrokeEvents: KeystrokeEventI[] | null,
	fieldInteractionEvents: FieldInteractionEventI[] | null,
): number | null => {
	if (keystrokeEvents && keystrokeEvents.length > 0) {
		const sum = keystrokeEvents.reduce((acc, e) => acc + e.timestamp, 0);
		return sum / keystrokeEvents.length;
	}
	// Fall back on file_upload / select_choice event timestamps
	if (fieldInteractionEvents && fieldInteractionEvents.length > 0) {
		const sum = fieldInteractionEvents.reduce((acc, e) => acc + e.timestamp, 0);
		return sum / fieldInteractionEvents.length;
	}
	return null;
};

export const buildTimelineEvents = (
	responses: GetApplicationOneResponseI["responses"],
	applicationEvents: ApplicationEventI[],
): TimelineEvent[] => {
	const events: TimelineEvent[] = [];

	for (const response of responses) {
		events.push({
			type: "field-response",
			timestamp: computeAverageKeystrokeTimestamp(
				response.keystroke_events,
				response.field_interaction_events,
			),
			response,
			keystrokeMetrics: response.keystroke_metrics,
			keystrokeEvents: response.keystroke_events,
		});
	}

	for (const appEvent of applicationEvents) {
		events.push({
			type: "app-event",
			timestamp: appEvent.timestamp,
			eventType: appEvent.type,
		});
	}

	events.sort((a, b) => {
		if (a.timestamp === null && b.timestamp === null) return 0;
		if (a.timestamp === null) return 1;
		if (b.timestamp === null) return -1;
		return a.timestamp - b.timestamp;
	});

	return events;
};

const FieldResponseNode = ({
	event,
	isLast,
}: {
	event: Extract<TimelineEvent, { type: "field-response" }>;
	isLast: boolean;
}) => {
	return (
		<TimelineItem>
			<TimelineOppositeContent
				sx={{ flex: 0.2, pt: 2 }}
				color="text.secondary"
				variant="body2"
			>
				{event.timestamp !== null ? formatTimestamp(event.timestamp) : ""}
			</TimelineOppositeContent>
			<TimelineSeparator>
				<TimelineDot color="primary" />
				{!isLast && <TimelineConnector />}
			</TimelineSeparator>
			<TimelineContent sx={{ pb: 3, pt: 1 }}>
				<Paper elevation={2} sx={{ p: 2 }}>
					<Box>
						{renderFieldView(
							event.response.field_identifier,
							event.response.field_type,
							event.response.field_label,
							event.response.field_description,
							event.response.value,
						)}
					</Box>
					{event.keystrokeMetrics && (
						<KeystrokeMetricsDisplay metrics={event.keystrokeMetrics} />
					)}
					{event.keystrokeEvents && event.keystrokeEvents.length > 1 && (
						<ReplayToggle events={event.keystrokeEvents} />
					)}
				</Paper>
			</TimelineContent>
		</TimelineItem>
	);
};

const APP_EVENT_LABELS: Record<string, string> = {
	left_tab: "Left tab",
	initial_start_before_submit: "Initial start (before submit)",
	initial_start_after_submit: "Initial start (after submit)",
	geolocation_success: "Geolocation granted",
	geolocation_denied: "Geolocation denied",
	geolocation_failure: "Geolocation failure",
	disclaimer_success: "Disclaimer accepted",
	show_intro_success: "Intro shown",
	form_next: "Next step",
};

const APP_EVENT_DOT_COLORS: Record<
	string,
	"success" | "error" | "warning" | "info" | "grey"
> = {
	left_tab: "warning",
	geolocation_success: "success",
	geolocation_denied: "error",
	geolocation_failure: "error",
	disclaimer_success: "success",
	show_intro_success: "info",
	form_next: "info",
	initial_start_before_submit: "grey",
	initial_start_after_submit: "grey",
};

const APP_EVENT_CHIP_COLORS: Record<
	string,
	"success" | "error" | "warning" | "info" | "default"
> = {
	left_tab: "warning",
	geolocation_success: "success",
	geolocation_denied: "error",
	geolocation_failure: "error",
	disclaimer_success: "success",
	show_intro_success: "info",
	form_next: "info",
	initial_start_before_submit: "default",
	initial_start_after_submit: "default",
};

const AppEventNode = ({
	event,
	isLast,
}: {
	event: Extract<TimelineEvent, { type: "app-event" }>;
	isLast: boolean;
}) => {
	const label = APP_EVENT_LABELS[event.eventType] ?? event.eventType;
	const dotColor = APP_EVENT_DOT_COLORS[event.eventType] ?? "grey";
	const chipColor = APP_EVENT_CHIP_COLORS[event.eventType] ?? "default";

	return (
		<TimelineItem>
			<TimelineOppositeContent
				sx={{ flex: 0.2, pt: 1.5 }}
				color="text.secondary"
				variant="body2"
			>
				{formatTimestamp(event.timestamp)}
			</TimelineOppositeContent>
			<TimelineSeparator>
				<TimelineDot color={dotColor} variant="outlined" />
				{!isLast && <TimelineConnector />}
			</TimelineSeparator>
			<TimelineContent sx={{ py: 1 }}>
				<Chip size="small" label={label} color={chipColor} variant="outlined" />
			</TimelineContent>
		</TimelineItem>
	);
};

export const RenderResponses = () => {
	const record: GetApplicationOneResponseI | undefined = useRecordContext();
	if (!record) return null;
	if (!record.responses) return null;

	const applicationEvents = record.application_events ?? [];

	const timelineEvents = buildTimelineEvents(
		record.responses,
		applicationEvents,
	);

	if (timelineEvents.length === 0) return null;

	return (
		<Timeline position="right" sx={{ p: 0 }}>
			{timelineEvents.map((event, i) => {
				const isLast = i === timelineEvents.length - 1;
				const key =
					event.type === "field-response"
						? `field-${event.response.field_step_id}_${event.response.field_identifier}`
						: `app-event-${event.eventType}-${event.timestamp}`;

				if (event.type === "field-response") {
					return <FieldResponseNode key={key} event={event} isLast={isLast} />;
				}
				return <AppEventNode key={key} event={event} isLast={isLast} />;
			})}
		</Timeline>
	);
};

const EarlySubmitWarnings = () => {
	const record: GetApplicationOneResponseI | undefined = useRecordContext();
	if (!record) return null;

	const warnings = [];

	if (record.has_left_tab) {
		warnings.push(
			<Alert severity="error" key="full-screen">
				⚠️ User has left the browser tab
			</Alert>,
		);
	}

	if (record.has_form_timer_expired) {
		warnings.push(
			<Alert severity="error" key="keyboard">
				⚠️ Response was automatically submitted due to form timer expiring
			</Alert>,
		);
	}

	if (warnings.length === 0) {
		return null;
	}

	return (
		<Stack spacing={1} mb={2}>
			{warnings}
		</Stack>
	);
};

export const analyticsButtonText = "Compare with other responses";

export const AnalyticsSection = () => {
	const record: GetApplicationOneResponseI | undefined = useRecordContext();
	const [showAnalytics, setShowAnalytics] = useState(false);

	if (!record) return null;

	// Reset showAnalytics to false when the application is not submitted
	const isSubmitted = record.has_finished_responding;
	const shouldShowAnalytics = showAnalytics && isSubmitted;

	return (
		<Box mt={3}>
			{!shouldShowAnalytics ? (
				<Tooltip
					title={
						isSubmitted
							? "Click to compare with other responses"
							: "Response not submitted yet"
					}
				>
					<span>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setShowAnalytics(true)}
							disabled={!isSubmitted}
						>
							{analyticsButtonText}
						</Button>
					</span>
				</Tooltip>
			) : (
				<>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => setShowAnalytics(false)}
						sx={{ mb: 2 }}
					>
						Hide comparison
					</Button>
					<ApplicationAnalytics applicationId={record.id} />
				</>
			)}
		</Box>
	);
};
const ShowArchiveButton = () => {
	const record = useRecordContext<GetApplicationOneResponseI>();
	const notify = useNotify();
	const refresh = useRefresh();
	const [update, { isLoading }] = useUpdate();

	if (!record) return null;

	const handleArchive = () => {
		update(
			"applications",
			{
				id: record.id,
				data: { is_archived: !record.is_archived },
				meta: { endpoint: `${record.id}` },
			},
			{
				onSuccess: () => {
					notify(
						record.is_archived
							? "Application unarchived successfully"
							: "Application archived successfully",
						{ type: "success" },
					);
					refresh();
				},
				onError: () => {
					notify("Error archiving application", { type: "error" });
				},
			},
		);
	};

	return (
		<Button
			onClick={handleArchive}
			disabled={isLoading}
			variant="outlined"
			size="small"
			color="primary"
		>
			{record.is_archived ? "Unarchive" : "Archive"}
		</Button>
	);
};

export const ApplicationsShow = (props: ComponentPropI) => {
	return (
		<Show disableAuthentication={props.isPublic}>
			<SimpleShowLayout>
				<Box display="flex" justifyContent="flex-end">
					<ShowArchiveButton />
				</Box>
				<TextField source="form_name" label="Form Name" />
				<TextField source="email" label="Respondent email" />
				<TextField source="created" label="Response submitted" />
				<TextField source="browser_user_agent" label="User Agent" />
				<TextField source="request_origin" label="Est. Geolocation (IP)" />
				<TextField source="browser_origin" label="Est. Geolocation (Browser)" />
				<FunctionField
					label="Form version ID"
					render={(record: GetApplicationOneResponseI) => (
						<div>
							{record.formVersionId} (Latest: {record.latestFormVersionId})
						</div>
					)}
				/>
				<BooleanField
					source="has_finished_responding"
					label="Has finished responding"
				/>
				<EarlySubmitWarnings />
				<RenderResponses />
				<AnalyticsSection />
			</SimpleShowLayout>
		</Show>
	);
};
